/**
 * runStatusVocabulary — the one Hebrew vocabulary for `AgentRun.status`.
 *
 * The field has a single producer and a closed scale: `AgentRunStatus` in
 * `backend/app/models/agent_run.py`, 15 arms, stored as a native Postgres enum, so no prose writer
 * can put a word in this column that the enum does not hold. What it has instead is a printer on
 * almost every operator screen, and each printer had grown a page-local map of its own subset: the
 * Guardian Center's run row carried 12 of the 15 arms, the platform-health card 11, the Guardian
 * audit line 10, and the human-handoff briefing had no map at all. An arm outside a given map fell
 * through it and printed the producer's own English identifier inside a Hebrew screen — which is how
 * `approved`, the status a remote session stamps on a run it opened for a ticket
 * (`manager_agent`, `browser_work_executor`), reached the Guardian row as `approved`.
 *
 * Two rules this module exists to hold:
 *
 *   1. One scale, re-derived from the producer — never a page-local subset.
 *      `runStatusVocabulary.test.js` parses the enum out of the Python source and asserts this map's
 *      key set EQUALS it, so a renamed arm or a 16th arm reddens the suite instead of shipping a raw
 *      token to an operator.
 *   2. An unrecognised value is NAMED, never echoed. A value outside the scale means this build is
 *      behind the producer; the operator needs a Hebrew word and a reason to look, not
 *      `waiting_for_customer_connection` on screen. The cost is that a producer rename reads as
 *      "unrecognised" until the map catches up — which is exactly what rule 1's pin is for.
 *
 * The named arm, rather than the passthrough `ticketCategoryVocabulary` uses, is the difference
 * between the two columns: `ai_category` is a varchar whose maintenance path stores whatever the
 * client sends, so a token nobody has swept may be real data worth showing verbatim; this column is
 * a closed enum, so a token outside the scale is never data.
 *
 * The trailing ellipsis the run panel's badge used ("מאתחל...") is dropped: this one map is read in
 * sentence contexts too ("סטטוס ריצה אחרונה: …"), where an ellipsis claims work still in progress.
 */

export const RUN_STATUS_HE = {
  pending:                         "מאתחל",
  analyzing:                       "מנתח קריאה",
  diagnosing:                      "מאבחן",
  awaiting_technician_review:      "ממתין לסקירה",
  awaiting_approval:               "ממתין לאישור",
  waiting_for_customer_connection: "ממתין לחיבור",
  connection_active:               "חיבור פעיל",
  waiting_for_customer_answer:     "ממתין לתשובת לקוח",
  approved:                        "מאושר",
  rejected:                        "נדחה",
  executing:                       "מבצע",
  paused:                          "הופסק",
  resolved:                        "נפתרה",
  completed:                       "הושלם",
  failed:                          "נכשל",
};

/** Shown for a value the scale does not hold, and for an absent one. Never the identifier itself. */
export const RUN_STATUS_UNKNOWN_HE = "מצב לא מזוהה";

/**
 * The Hebrew word for a stored run status. `hasOwnProperty` rather than `RUN_STATUS_HE[value] ||`:
 * a bare lookup answers an inherited key (`"constructor"`, `"toString"`) with a function, which the
 * `||` fallback would treat as a hit and render.
 */
export function runStatusLabel(value) {
  return Object.prototype.hasOwnProperty.call(RUN_STATUS_HE, value)
    ? RUN_STATUS_HE[value]
    : RUN_STATUS_UNKNOWN_HE;
}
